import React, { useCallback, useEffect, useRef, useState } from "react";
import { Toaster, toast } from "sonner";
import { Sliders, FileText, Loader2, CreditCard, Clock, AlertTriangle } from "lucide-react";
import "./App.css";
import { getState, postMode, postCommand, postAccountMode, errText } from "./api";
import { useAuth } from "./auth/AuthContext";
import Login from "./components/Login";
import PaymentModal from "./components/dashboard/PaymentModal";

import Header from "./components/dashboard/Header";
import Sidebar from "./components/dashboard/Sidebar";
import KpiCards from "./components/dashboard/KpiCards";
import PriceChart from "./components/dashboard/PriceChart";
import AiConsensus from "./components/dashboard/AiConsensus";
import AiOpinion from "./components/dashboard/AiOpinion";
import Advisor from "./components/dashboard/Advisor";
import PositionsTable from "./components/dashboard/PositionsTable";
import RiskGuard from "./components/dashboard/RiskGuard";
import ActivityLog from "./components/dashboard/ActivityLog";
import AddSignalDialog from "./components/dashboard/AddSignalDialog";
import SecurityPanel from "./components/dashboard/SecurityPanel";
import MobileNavDrawer from "./components/dashboard/MobileNavDrawer";
import BottomTabBar from "./components/dashboard/BottomTabBar";

function App() {
  const { user, ready, signOut, refreshUser } = useAuth();

  if (!ready) {
    return (
      <div className="min-h-screen grid place-items-center text-slate-400">
        <Loader2 className="animate-spin text-amber-400" size={28} />
      </div>
    );
  }
  if (!user) return <Login />;
  return <Dashboard user={user} signOut={signOut} refreshUser={refreshUser} />;
}

function Dashboard({ user, signOut, refreshUser }) {
  const [snapshot, setSnapshot] = useState(null);
  const [active, setActive] = useState("overview");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const [payMode, setPayMode] = useState(null); // "license" | "create_user" | null
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const timer = useRef(null);
  const canTrade = user.role !== "readonly";

  const navigate = (id) => {
    setActive(id);
    setMobileNavOpen(false);
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 84;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  const refresh = useCallback(async () => {
    try {
      const data = await getState();
      setSnapshot(data);
    } catch (e) {
      if (e?.response?.status === 401) signOut();
    }
  }, [signOut]);

  useEffect(() => {
    refresh();
    timer.current = setInterval(refresh, 1500);
    return () => clearInterval(timer.current);
  }, [refresh]);

  const handleAccountMode = async (mode) => {
    if (mode === "real" && !window.confirm("Passare al CONTO REALE? Assicurati che il bridge sia collegato e Live verificato.")) return;
    try {
      await postAccountMode(mode);
      toast[mode === "real" ? "warning" : "success"](mode === "real" ? "Conto REALE attivo" : "Conto DEMO attivo");
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handleToggleLive = async (enabled) => {
    if (enabled) {
      const ok = window.confirm(
        "Abilitare la modalità LIVE per XAUUSD? Il bridge potrà ricevere nuovi ordini approvati dal consenso AI e dal risk guard."
      );
      if (!ok) return;
    }
    try {
      await postMode({ enabled, confirmation: enabled ? "ENABLE_LIVE_XAUUSD" : "DISABLE_LIVE_XAUUSD" });
      toast[enabled ? "warning" : "success"](
        enabled ? "Modalità LIVE attiva dopo i controlli del server" : "Modalità Paper attiva: nuovi ordini live bloccati"
      );
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handleKill = async () => {
    if (!window.confirm("Kill switch: disabilitare subito il LIVE? Le posizioni aperte non verranno chiuse automaticamente.")) return;
    try {
      await postMode({ enabled: false, confirmation: "DISABLE_LIVE_XAUUSD" });
      toast.warning("Kill switch attivo: nuovi ordini live bloccati");
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handlePanic = async () => {
    if (!window.confirm("🚨 PANIC: chiudo TUTTE le posizioni XAUUSD e disattivo il Live. Confermi?")) return;
    try {
      await postCommand({ type: "CLOSE_ALL" });
      await postMode({ enabled: false, confirmation: "DISABLE_LIVE_XAUUSD" });
      toast.warning("PANIC eseguito: tutte le posizioni chiuse e Live disattivato");
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handleClose = async (positionId) => {
    try {
      await postCommand({ type: "CLOSE_POSITION", positionId });
      toast.success(`Posizione #${positionId} chiusa`);
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const handleCloseAll = async () => {
    if (!window.confirm("Chiudere tutte le posizioni XAUUSD aperte?")) return;
    try {
      await postCommand({ type: "CLOSE_ALL" });
      toast.success("Chiusura totale eseguita");
      refresh();
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const openNew = () => {
    setPrefill(null);
    setDialogOpen(true);
  };
  const applyAdvisor = (setup) => {
    setPrefill(setup);
    setDialogOpen(true);
  };

  return (
    <div className="min-h-screen grain">
      <Toaster
        position="bottom-right"
        theme="dark"
        toastOptions={{
          style: {
            background: "#121824",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "#F9FAFB",
            fontFamily: "Inter, sans-serif",
          },
        }}
      />
      <Header
        snapshot={snapshot}
        user={user}
        onLogout={signOut}
        onAccountMode={handleAccountMode}
        onToggleLive={handleToggleLive}
        onKill={handleKill}
        onPanic={handlePanic}
        onMenu={() => setMobileNavOpen(true)}
        onNewSignal={openNew}
      />
      <Sidebar active={active} onNavigate={navigate} snapshot={snapshot} />
      <MobileNavDrawer
        open={mobileNavOpen}
        active={active}
        onNavigate={navigate}
        onClose={() => setMobileNavOpen(false)}
        snapshot={snapshot}
      />

      <main className="lg:pl-64 pt-5 px-3 sm:px-4 lg:px-6 pb-24 lg:pb-16 space-y-5 sm:space-y-6 relative z-10">
        <LicenseBanner user={user} onBuy={() => setPayMode("license")} />
        <KpiCards snapshot={snapshot} />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <PriceChart snapshot={snapshot} onNewSignal={canTrade ? openNew : () => toast.info("Ruolo sola-lettura")} />
          </div>
          <div className="lg:col-span-4 space-y-6">
            <AiOpinion onCreateSignal={applyAdvisor} canTrade={canTrade} onSent={refresh} />
            <AiConsensus snapshot={snapshot} />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8 space-y-6">
            <PositionsTable
              snapshot={snapshot}
              readOnly={!canTrade}
              onClose={handleClose}
              onCloseAll={handleCloseAll}
            />
            <ActivityLog snapshot={snapshot} />
          </div>
          <div className="lg:col-span-4 space-y-6">
            <Advisor onApply={applyAdvisor} canTrade={canTrade} />
            <RiskGuard snapshot={snapshot} />
          </div>
        </div>

        <SecurityPanel snapshot={snapshot} onCreatePaidUser={() => setPayMode("create_user")} />
        <Settings snapshot={snapshot} />
        <Documentation />
      </main>

      <AddSignalDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        prefill={prefill}
        marketBid={snapshot?.market?.bid}
        onSubmitted={refresh}
      />
      <PaymentModal
        open={!!payMode}
        mode={payMode}
        onClose={() => setPayMode(null)}
        onDone={() => { refreshUser(); refresh(); }}
      />
      <BottomTabBar active={active} onNavigate={navigate} onNewSignal={openNew} canTrade={canTrade} />
    </div>
  );
}

function LicenseBanner({ user, onBuy }) {
  if (user.role === "admin") return null;
  const expires = user.accessExpiresAt ? new Date(user.accessExpiresAt) : null;
  const daysLeft = expires ? Math.ceil((expires - new Date()) / 86400000) : 0;

  if (user.status === "pending") {
    return (
      <div data-testid="license-banner-pending" className="rounded-xl border border-amber-500/30 bg-amber-500/[0.08] px-5 py-3.5 flex items-center gap-3 float-in">
        <Clock size={18} className="text-amber-400 shrink-0" />
        <div className="text-[12px] text-amber-200">
          <b>Account in attesa di approvazione.</b> Un amministratore deve autorizzare il tuo accesso trader prima di poter operare.
        </div>
      </div>
    );
  }
  if (!user.active) {
    return (
      <div data-testid="license-banner-expired" className="rounded-xl border border-rose-500/30 bg-rose-500/[0.08] px-5 py-3.5 flex items-center justify-between gap-3 float-in">
        <div className="flex items-center gap-3">
          <AlertTriangle size={18} className="text-rose-400 shrink-0" />
          <div className="text-[12px] text-rose-200">
            <b>Licenza di accesso scaduta o assente.</b> {user.role === "trader" ? "Acquista giorni di accesso (10€/giorno) per operare." : "Contatta il tuo trader per rinnovare l'accesso."}
          </div>
        </div>
        {user.role === "trader" && (
          <button data-testid="buy-license-button" onClick={onBuy}
            className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400">
            <CreditCard size={14} /> Acquista licenza
          </button>
        )}
      </div>
    );
  }
  if (user.role === "trader" && daysLeft <= 5) {
    return (
      <div data-testid="license-banner-warning" className="rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-5 py-3 flex items-center justify-between gap-3 float-in">
        <div className="flex items-center gap-3 text-[12px] text-amber-200">
          <Clock size={16} className="text-amber-400" /> Licenza attiva · scade tra <b>{daysLeft} giorni</b>
        </div>
        <button data-testid="buy-license-button" onClick={onBuy}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-[#0B0E17] bg-gradient-to-br from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400">
          <CreditCard size={13} /> Rinnova
        </button>
      </div>
    );
  }
  return null;
}

function Settings({ snapshot }) {
  const cfg = snapshot?.config || {};
  const items = [
    ["Lotti minimi", cfg.minLots],
    ["Lotti massimi", cfg.maxLots],
    ["Segnali attivi max", cfg.maxOpenSignals],
    ["Perdita giornaliera max", `${cfg.maxDailyLossPct}%`],
    ["Spread massimo", cfg.maxSpread],
    ["Confidenza AI minima", `${Math.round((cfg.minAiConfidence || 0) * 100)}%`],
  ];
  return (
    <div id="impostazioni" data-testid="settings-panel" className="panel p-5 float-in">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
        <span className="w-6 h-6 rounded-md grid place-items-center bg-slate-500/15 text-slate-300">
          <Sliders size={13} />
        </span>
        Impostazioni MT5 (server)
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {items.map(([k, v], i) => (
          <div key={i} className="rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
            <span className="block text-[9px] uppercase tracking-wider text-slate-500 mb-1">{k}</span>
            <b className="font-mono text-[13px] text-amber-400">{v ?? "—"}</b>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
        I parametri operativi sono gestiti dalle variabili d'ambiente del server Linux. Il Live parte sempre
        disattivato dopo un riavvio e richiede conferma esplicita.
      </p>
    </div>
  );
}

function Documentation() {
  const steps = [
    "Avvia il server API su Linux (questo servizio) dietro HTTPS.",
    "Sulla macchina Windows con MetaTrader 5 avvia il bridge Python (cartella windows-bridge).",
    "Verifica il simbolo reale del broker (XAUUSD o suffisso es. XAUUSDm) e testa con MT5_DRY_RUN=true.",
    "Testa su conto demo, poi abilita il Live con la conferma esplicita e mantieni il kill switch.",
  ];
  return (
    <div id="documentazione" data-testid="documentation-panel" className="panel p-5 float-in">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-4">
        <span className="w-6 h-6 rounded-md grid place-items-center bg-slate-500/15 text-slate-300">
          <FileText size={13} />
        </span>
        Documentazione · Deploy reale
      </div>
      <div className="grid gap-2.5">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-3 items-start rounded-lg border border-white/8 bg-white/[0.02] px-3 py-2.5">
            <span className="w-5 h-5 rounded-full grid place-items-center bg-amber-500/15 text-amber-400 text-[10px] font-bold shrink-0">
              {i + 1}
            </span>
            <span className="text-[11px] text-slate-300 leading-relaxed">{s}</span>
          </div>
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
        In questa anteprima cloud il feed di mercato XAUUSD e l'esecuzione MT5 sono <b className="text-amber-400">simulati</b> in
        tempo reale. Il codice Node.js + bridge Windows per il trading reale è disponibile in <span className="font-mono">/app/legacy-node-stack</span>.
      </p>
    </div>
  );
}

export default App;
